import type { LookupServiceProviderType } from '@/types';

function formatMessage(provider: LookupServiceProviderType, message: string) {
  return `🔎 [${provider}] ${message}`;
}

class BaseLookupError extends Error {
  public readonly rawMessage: string;

  constructor(provider: LookupServiceProviderType, message: string) {
    super(formatMessage(provider, message));
    this.rawMessage = message;
  }
}

export class ProviderSessionExpiredError extends BaseLookupError {
  constructor(provider: LookupServiceProviderType, message: string) {
    super(provider, message);
    this.name = "ProviderSessionExpiredError";
  }
}

export class ProviderAuthenticationError extends BaseLookupError {
  constructor(provider: LookupServiceProviderType, message: string) {
    super(provider, message);
    this.name = "ProviderAuthenticationError";
  }
}

export class ProviderCallsignNotFoundError extends BaseLookupError {
  constructor(provider: LookupServiceProviderType, message: string) {
    super(provider, message);
    this.name = "ProviderCallsignNotFoundError";
  }
}

/**
 * The lookup service couldn't be reached (offline, DNS failure, host
 * unreachable, connection reset). A transient, expected condition -- callers
 * should surface it to the user, not report it to Sentry.
 */
export class ProviderUnavailableError extends BaseLookupError {
  constructor(
    provider: LookupServiceProviderType,
    message: string = "Couldn't reach the lookup service. Check your connection."
  ) {
    super(provider, message);
    this.name = "ProviderUnavailableError";
  }
}

/**
 * Callsign lookups are turned off in settings. Not an error condition -- the
 * user opted out -- so callers should quietly skip, never report to Sentry.
 */
export class LookupsDisabledError extends BaseLookupError {
  constructor(
    provider: LookupServiceProviderType,
    message: string = "Callsign lookups are disabled in settings."
  ) {
    super(provider, message);
    this.name = "LookupsDisabledError";
  }
}

/**
 * True when an error is a browser fetch network failure. The message differs by
 * engine: Chromium/Electron "Failed to fetch", WebKit/iOS "Load failed",
 * Firefox "NetworkError when attempting to fetch resource".
 */
export function isLookupNetworkError(error: unknown): boolean {
  if (!(error instanceof TypeError)) return false;
  const message = error.message.toLowerCase();
  return (
    message.includes("failed to fetch") ||
    message.includes("load failed") ||
    message.includes("networkerror") ||
    message.includes("network request failed")
  );
}

export default class LookupServiceError extends BaseLookupError {
  constructor(
    public service: LookupServiceProviderType,
    public code: string,
    message: string
  ) {
    super(service, message);
  }
}
